/**
 * MXlab — Validation maison des champs obligatoires du formulaire panier.
 *
 * Pourquoi maison : la validation native affiche une bulle sur le 1er champ invalide
 * et fait defiler la page — quand le client a clique sur le bouton COLLANT depuis le
 * bas, il voit la page bouger et la bulle disparaitre sans comprendre.
 *
 *  - noValidate pose DEPUIS LE JS (jamais en Liquid) : si ce script ne se charge pas,
 *    la validation native reprend la main => jamais de commande sans modele ni annee.
 *  - Submit intercepte en window/capture. Ce module s'execute AVANT mx-options (rendu
 *    plus loin dans le DOM) => listener pose en 1er => stopImmediatePropagation bloque
 *    mx-options ET le natif si invalide.
 *  - Detection GENERIQUE : controles `required` rattaches au form, regroupes sous un
 *    [data-mx-required]. Un futur champ obligatoire marque pareil marche sans code neuf.
 *  - A11y : aria-invalid + aria-describedby sur les champs ; encart panneau role=alert.
 */
const WRAP = '[data-mx-required]';
const ZONE_SANS_VARIANTE = '[data-mx-no-variant]';
let live = false; // effacement en direct actif apres un 1er submit invalide

const formOf = (el) => el && el.form;

/** Pose noValidate sur chaque form portant un champ obligatoire gere. */
function init() {
  document.querySelectorAll(WRAP).forEach((wrap) => {
    const form = formOf(wrap.querySelector('input, select, textarea'));
    if (form) form.noValidate = true;
  });
  document.querySelectorAll(ZONE_SANS_VARIANTE).forEach(isolerDuVariantPicker);
}

/**
 * Isole les champs MAISON rendus DANS `<variant-picker>` (l'etape « Ta moto » : decoupe,
 * notes, listes heritees). Le selecteur natif ecoute `change` sur lui-meme et traite TOUT
 * changement comme un changement d'option : il refait un fetch de la section, morphe, et
 * comme le champ ne porte ni valeur d'option ni variante, il RETIRE `?variant=` de l'URL —
 * un lien partage ou un rafraichissement perdait alors la selection.
 *
 * On arrete donc a la frontiere de la zone les `change` qui ne portent pas de valeur
 * d'option. Ceux de la cylindree en portent une : ils passent, et le natif fait son travail.
 * `stopPropagation` (et non `stopImmediatePropagation`) : les ecouteurs de la zone
 * elle-meme, dont ceux du selecteur de decoupe, s'executent normalement. La validation,
 * elle, ecoute en CAPTURE sur `document` : elle voit l'evenement avant cet arret.
 *
 * @param {Element} zone
 */
function isolerDuVariantPicker(zone) {
  zone.addEventListener('change', (event) => {
    const cible = event.target;
    if (cible && cible.dataset && cible.dataset.optionValueId) return;
    event.stopPropagation();
  });
}

/** Wrappers [data-mx-required] dont un controle require est rattache au form. */
function wrappers(form) {
  const map = new Map(); // wrapper -> Set(names)
  for (const el of form.elements) {
    if (!el.name || !el.required || !el.closest) continue;
    const w = el.closest(WRAP);
    if (!w) continue;
    if (!map.has(w)) map.set(w, new Set());
    map.get(w).add(el.name);
  }
  return map;
}

/** Champ manquant ? Radio group => aucune valeur cochee ; sinon checkValidity(). */
function missing(form, name) {
  const node = form.elements[name];
  if (!node) return false;
  if (typeof RadioNodeList !== 'undefined' && node instanceof RadioNodeList) {
    return node.value === '';
  }
  return typeof node.checkValidity === 'function' ? !node.checkValidity() : false;
}

/** Applique/retire l'etat d'erreur d'un champ (message + aria). */
function markField(wrap, on) {
  const msg = wrap.querySelector('[data-mx-field-msg]');
  wrap.querySelectorAll('input, select, textarea').forEach((c) => {
    if (on) {
      c.setAttribute('aria-invalid', 'true');
      if (msg && msg.id) c.setAttribute('aria-describedby', msg.id);
    } else {
      c.removeAttribute('aria-invalid');
      if (msg && msg.id) c.removeAttribute('aria-describedby');
    }
  });
  if (msg) {
    msg.textContent = on ? wrap.getAttribute('data-mx-field-error') || '' : '';
    msg.hidden = !on;
  }
}

/** Defile + focus le champ (block:center => hors en-tete collant). */
function reveal(wrap) {
  wrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
  const control = wrap.querySelector('input, select, textarea');
  if (control) control.focus({ preventScroll: true });
}

/** (Re)construit l'encart du panneau a partir des wrappers manquants. */
function renderPanel(missingWraps) {
  const panel = document.querySelector('[data-mx-validate-panel]');
  if (!panel) return;
  if (!missingWraps.length) {
    panel.replaceChildren();
    panel.hidden = true;
    return;
  }
  const n = missingWraps.length;
  const tpl = (n === 1 ? panel.dataset.summaryOne : panel.dataset.summaryMany) || '{n}';

  const box = document.createElement('div');
  box.className = 'mx-validate';

  const head = document.createElement('p');
  head.className = 'mx-validate__head';
  head.textContent = tpl.replace('{n}', n);
  box.appendChild(head);

  const list = document.createElement('ul');
  list.className = 'mx-validate__list';
  missingWraps.forEach((wrap) => {
    const li = document.createElement('li');
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'mx-validate__link';
    btn.textContent = wrap.getAttribute('data-mx-field-label') || '';
    btn.addEventListener('click', () => reveal(wrap));
    li.appendChild(btn);
    list.appendChild(li);
  });
  box.appendChild(list);

  panel.hidden = false;
  panel.replaceChildren(box);
}

/** Valide le form : marque chaque champ, met a jour l'encart, renvoie les manquants. */
function validate(form, fromSubmit = false) {
  const missingWraps = [];
  for (const [wrap, names] of wrappers(form)) {
    const isMissing = [...names].some((name) => missing(form, name));
    markField(wrap, isMissing);
    if (isMissing) missingWraps.push(wrap);
  }
  renderPanel(missingWraps);
  // Signal ONE-WAY vers la barre mobile (mx-buybar) : elle affiche un marqueur compact
  // et se déplie si fromSubmit. La barre ne connaît PAS les règles de validation ;
  // c'est la validation qui pilote la barre, jamais l'inverse.
  document.dispatchEvent(
    new CustomEvent('mx:validation', { detail: { count: missingWraps.length, fromSubmit } })
  );
  return missingWraps;
}

function onSubmit(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !wrappers(form).size) return;
  form.noValidate = true; // reassurance
  const missingWraps = validate(form, true);
  if (missingWraps.length) {
    event.preventDefault();
    event.stopImmediatePropagation();
    live = true;
    reveal(missingWraps[0]);
  }
}

function onFieldChange(event) {
  if (!live) return;
  const wrap = event.target && event.target.closest ? event.target.closest(WRAP) : null;
  if (!wrap) return;
  const form = formOf(event.target);
  if (form) validate(form);
}

// Enregistrement au top-level (pas dans un custom element) : listener window/capture
// pose des l'execution du module, avant mx-options.
//
// INVARIANT PANIER (ordre des intercepteurs) — NE PAS CASSER : ce module DOIT s'exécuter
// AVANT mx-options pour que son listener soit posé en 1er et gagne le stopImmediatePropagation
// quand le formulaire est invalide. C'est garanti par l'ordre du DOM : mx-validate est rendu
// dans la SECTION, avant la grille (donc avant le <script> de mx-options, bloc de la colonne).
// NE PAS déplacer ce rendu après la grille, NE PAS passer cet enregistrement dans un custom
// element / connectedCallback (il perdrait sa priorité), NE PAS rendre onSubmit async
// (stopImmediatePropagation doit être synchrone). `novalidate` est posé DEPUIS le JS : si ce
// module ne charge pas, la validation NATIVE (required) reprend → jamais de commande sans
// modele/annee.
window.addEventListener('submit', onSubmit, { capture: true });
document.addEventListener('change', onFieldChange, true);
document.addEventListener('input', onFieldChange, true);
init();
